import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { API, useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Search, Archive, ArchiveRestore, RefreshCw, Download, ExternalLink, Lock, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDateBR } from "@/lib/dates";
import TaskFormDialog from "@/components/TaskFormDialog";
import { normalizeTask, phase1Api } from "@/lib/phase1Api";
import { DADOS } from "../lib/larguras";

const FILTERS = [
  { label: "Ativas", value: "false" },
  { label: "Arquivadas", value: "true" },
  { label: "Todas", value: "" },
];

export default function IntimacoesPanel() {
  const { user } = useAuth();
  const [intimacoes, setIntimacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("false");
  const [search, setSearch] = useState("");
  const [tribunal, setTribunal] = useState("");
  const [tipo, setTipo] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [opcoes, setOpcoes] = useState({ tribunais: [], tipos: [] });
  const [bloqueadas, setBloqueadas] = useState({ total: 0, items: [] });
  // A lista vazia tem três causas diferentes, e dizer a errada é pior do que
  // não dizer nada: mandar "cadastre sua OAB" para quem já cadastrou e cuja
  // consulta ao Diário falhou faz o advogado procurar defeito onde não há.
  const [oabs, setOabs] = useState(null);
  const pageSize = 30;
  const sentinelRef = React.useRef(null);
  const [selected, setSelected] = useState(null);
  const [taskForm, setTaskForm] = useState(null);
  const [savingTask, setSavingTask] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersUnavailable, setMembersUnavailable] = useState(false);

  const load = async (goPage = 1, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = { page: goPage, page_size: pageSize };
      if (filter !== "") params.arquivada = filter === "true";
      if (search) params.search = search;
      if (tribunal) params.tribunal = tribunal;
      if (tipo) params.tipo = tipo;
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      const res = await axios.get(`${API}/intimacoes`, { params, withCredentials: true });
      const items = (Array.isArray(res.data?.items) ? res.data.items : []).map((item) => {
        const projectedTask = normalizeTask(item.task || item.tarefa || item.task_card || item.kanban_card || item.tarefa_vinculada);
        return projectedTask ? { ...item, task: projectedTask } : item;
      });
      setIntimacoes((prev) => append ? [...prev, ...items] : items);
      setTotal(res.data?.total ?? 0);
      setPage(res.data?.page ?? goPage);
    } catch {
      toast.error("Erro ao carregar intimações");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // null = ainda carregando: não afirma nada antes de saber.
  const semOab = oabs !== null && oabs.length === 0;
  const falhaNaConsulta = oabs !== null && oabs.length > 0 && oabs.some((o) => o.last_sync_error);

  useEffect(() => {
    axios.get(`${API}/intimacoes/filtros`, { withCredentials: true })
      .then((res) => setOpcoes({ tribunais: res.data?.tribunais || [], tipos: res.data?.tipos || [] }))
      .catch(() => {});
    axios.get(`${API}/intimacoes/bloqueadas`, { withCredentials: true })
      .then((res) => setBloqueadas({ total: res.data?.total || 0, items: res.data?.items || [] }))
      .catch(() => {});
    axios.get(`${API}/oabs`, { withCredentials: true })
      .then((res) => setOabs(res.data?.oabs || res.data || []))
      .catch(() => setOabs([]));
  }, []);

  useEffect(() => {
    phase1Api.getBoardMembers()
      .then((items) => {
        if (items.length) {
          setMembers(items);
          setMembersUnavailable(false);
        } else {
          setMembers(user ? [user] : []);
          setMembersUnavailable(true);
        }
      })
      .catch(() => {
        setMembers(user ? [user] : []);
        setMembersUnavailable(true);
      });
  }, [user]);

  // Recarrega ao mudar filtros (volta pra página 1)
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, tribunal, tipo, dataInicio, dataFim]);

  const handleSearch = (e) => {
    e.preventDefault();
    load(1);
  };

  const hasMore = intimacoes.length < total;

  // Lazy load: carrega a próxima página quando o sentinela entra na viewport
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) load(page + 1, true);
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, page]);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/intimacoes/sync-all`, {}, { withCredentials: true });
      toast.success("Sincronização iniciada em background");
      setTimeout(load, 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const handleArchive = async (intimacaoId, isArchived) => {
    const action = isArchived ? "unarchive" : "archive";
    try {
      await axios.post(`${API}/intimacoes/${intimacaoId}/${action}`, {}, { withCredentials: true });
      toast.success(isArchived ? "Intimação reativada" : "Intimação arquivada");
      setIntimacoes((prev) => prev.filter((i) => i.intimacao_id !== intimacaoId));
      if (selected?.intimacao_id === intimacaoId) setSelected(null);
    } catch {
      toast.error("Erro ao atualizar intimação");
    }
  };

  const handleMarkRead = async (intimacaoId) => {
    try {
      await axios.post(`${API}/intimacoes/${intimacaoId}/read`, {}, { withCredentials: true });
      setIntimacoes((prev) =>
        prev.map((i) => i.intimacao_id === intimacaoId ? { ...i, lida: true } : i)
      );
    } catch {}
  };

  const openIntimacao = (int) => {
    setSelected(int);
    if (!int.lida) handleMarkRead(int.intimacao_id);
  };

  const openTaskForm = () => {
    if (!selected) return;
    const existing = selected.task || selected.kanban_card || selected.task_card;
    if (existing) return;
    setTaskForm({
      title: selected.titulo || `Intimação — ${selected.numero_processo || "sem processo"}`,
      descricao: selected.resumo || "",
      prioridade: "",
      data_limite: selected.data_limite || "",
      responsavel_id: user?.user_id || "",
      processo_id: selected.processo_id || null,
      processo_numero: selected.numero_processo || null,
      intimacao_id: selected.intimacao_id,
    });
  };

  const restoreCancelledTask = async (task) => {
    if (!task?.card_id) return;
    try {
      const response = await phase1Api.restoreCard(task.card_id);
      const restored = normalizeTask(response.data) || { ...task, cancelled: false };
      setSelected((current) => ({ ...current, task: restored }));
      setIntimacoes((items) => items.map((item) => item.intimacao_id === selected.intimacao_id ? { ...item, task: restored } : item));
      toast.success("Tarefa restaurada no Kanban");
      openLinkedTask(restored);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível restaurar a tarefa");
    }
  };

  const createTask = async (values) => {
    if (!selected) return;
    setSavingTask(true);
    try {
      const response = await phase1Api.createTaskFromIntimation(selected.intimacao_id, {
        title: values.title,
        descricao: values.descricao || null,
        prioridade: values.prioridade,
        data_limite: values.data_limite,
        responsavel_id: values.responsavel_id || null,
        processo_id: values.processo_id || null,
        stage_id: values.stage_id || null,
      });
      const task = normalizeTask(response.data);
      setSelected((current) => ({ ...current, task }));
      setIntimacoes((items) => items.map((item) => item.intimacao_id === selected.intimacao_id ? { ...item, task } : item));
      setTaskForm(null);
      toast.success("Tarefa criada no Kanban");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível criar a tarefa");
    } finally {
      setSavingTask(false);
    }
  };

  const openLinkedTask = (task) => {
    if (task?.card_id) navigate(`/quadros?card_id=${encodeURIComponent(task.card_id)}`);
  };

  const linkedTask = selected?.task || selected?.kanban_card || selected?.task_card;
  const linkedTaskCancelled = linkedTask?.cancelled === true;

  return (
    <Layout>
      <div className={DADOS}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in-1">
          <div>
            <p className="text-[#335480] text-sm uppercase tracking-widest font-medium mb-1">DJEN</p>
            <h1 className="text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Intimações
            </h1>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="export-intimacoes-pdf-btn"
              onClick={() => window.open(`${API}/export/intimacoes/pdf?arquivada=${filter === "" ? "" : filter}`, "_blank")}
              variant="outline"
              className="border-[#D1DEEE] text-[#335480] hover:text-[#003882] h-11 font-semibold"
            >
              <Download size={18} />
              Exportar PDF
            </Button>
            <Button
              data-testid="sync-intimacoes-btn"
              onClick={handleSyncAll}
              disabled={syncing}
              className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-11 px-6"
            >
              <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Sincronizando..." : "Sincronizar DJEN"}
            </Button>
          </div>
        </div>

        {/* Filters + Search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6 animate-fade-in-2">
          <div className="flex border border-[#D1DEEE] rounded-lg overflow-hidden bg-white">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                data-testid={`filter-${f.label.toLowerCase()}`}
                onClick={() => setFilter(f.value)}
                className={`px-5 py-2.5 text-base font-medium transition-colors ${
                  filter === f.value
                    ? "bg-[#003882] text-white"
                    : "text-[#335480] hover:bg-[#F4F7FB]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSearch} className="flex gap-2 flex-1">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#335480]" />
              <Input
                data-testid="search-intimacoes-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por número, órgão..."
                className="pl-9 h-10 text-base border-[#D1DEEE]"
              />
            </div>
            <Button type="submit" variant="outline" className="border-[#D1DEEE] h-10">
              Buscar
            </Button>
          </form>
        </div>

        {/* Filtros avançados */}
        <div className="flex flex-wrap gap-3 mb-6 -mt-2">
          <select value={tribunal} onChange={(e) => setTribunal(e.target.value)}
            className="h-10 px-3 border border-[#D1DEEE] rounded-lg bg-white text-sm text-[#335480]">
            <option value="">Todos os tribunais</option>
            {opcoes.tribunais.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}
            className="h-10 px-3 border border-[#D1DEEE] rounded-lg bg-white text-sm text-[#335480] max-w-[220px]">
            <option value="">Todos os tipos</option>
            {opcoes.tipos.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#335480]">de</span>
            <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="h-10 border-[#D1DEEE] w-[150px]" />
            <span className="text-sm text-[#335480]">até</span>
            <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="h-10 border-[#D1DEEE] w-[150px]" />
          </div>
          {(tribunal || tipo || dataInicio || dataFim) && (
            <button onClick={() => { setTribunal(""); setTipo(""); setDataInicio(""); setDataFim(""); }}
              className="h-10 px-3 text-sm text-[#003882] hover:underline">Limpar filtros</button>
          )}
          <span className="ml-auto text-sm text-[#335480] self-center">{total} intimação(ões)</span>
        </div>

        {/* Teaser FOMO — intimações bloqueadas de processos não monitorados */}
        {bloqueadas.total > 0 && (
          <div className="mb-6 rounded-xl border-2 border-amber-300 bg-amber-50 overflow-hidden">
            <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 bg-amber-100/60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center">
                  <Lock size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-[#001533] font-bold text-base">
                    {bloqueadas.total} intimaç{bloqueadas.total !== 1 ? "ões" : "ão"} de processos não monitorados
                  </p>
                  <p className="text-[#92670a] text-sm">Você atingiu o limite do plano FREE. Faça upgrade para desbloquear e monitorar.</p>
                </div>
              </div>
              <Button onClick={() => navigate("/plano")} className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold">
                <Crown size={16} /> Desbloquear no PRO
              </Button>
            </div>
            <div className="divide-y divide-amber-200/60">
              {bloqueadas.items.slice(0, 5).map((b, i) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[#001533] font-mono text-sm truncate">{b.numero_processo || "—"}</p>
                    <p className="text-[#92670a] text-xs truncate">{b.tipo_comunicacao || "Intimação"} · {b.orgao_julgador || b.orgao || "—"} · {formatDateBR(b.data_disponibilizacao)}</p>
                  </div>
                  <Lock size={14} className="text-amber-500 flex-shrink-0" />
                </div>
              ))}
              {bloqueadas.total > 5 && (
                <div className="px-5 py-2.5 text-center text-[#92670a] text-sm">
                  + {bloqueadas.total - 5} outras bloqueadas
                </div>
              )}
            </div>
          </div>
        )}

        {/* List */}
        <div className="bg-white border border-[#D1DEEE] rounded-lg overflow-hidden animate-fade-in-3">
          {loading ? (
            <div className="p-8 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-[#F4F7FB] rounded animate-pulse" />
              ))}
            </div>
          ) : intimacoes.length === 0 ? (
            <div className="p-16 text-center">
              <Bell size={48} className="mx-auto mb-4 text-[#D1DEEE]" />
              <h3 className="text-xl font-semibold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>
                {filter === "true"
                  ? "Nenhuma intimação arquivada"
                  : falhaNaConsulta
                    ? "Não conseguimos consultar o Diário"
                    : "Nenhuma intimação"}
              </h3>
              <p className="text-[#335480] text-base" data-testid="intimacoes-vazio">
                {filter === "true"
                  ? "Intimações arquivadas aparecerão aqui."
                  : semOab
                    ? "Cadastre suas OABs e sincronize para receber intimações do DJEN."
                    : falhaNaConsulta
                      ? "Sua OAB está cadastrada corretamente — a falha foi no acesso à fonte oficial. Tentamos de novo automaticamente."
                      : "Nenhuma intimação no período. Suas OABs estão sendo monitoradas."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[#D1DEEE]">
              {intimacoes.map((int, i) => (
                <div
                  key={int.intimacao_id}
                  data-testid={`intimacao-row-${i}`}
                  className={`px-6 py-4 hover:bg-[#F4F7FB] transition-colors cursor-pointer ${
                    !int.lida ? "border-l-4 border-l-[#003882]" : ""
                  }`}
                  onClick={() => openIntimacao(int)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="text-[#001533] font-semibold text-base">
                          {int.numero_processo || "Processo não informado"}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#e5edf5] text-[#003882] font-medium">
                          {int.tipo_comunicacao}
                        </span>
                        {!int.lida && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#003882] text-white font-medium">
                            Nova
                          </span>
                        )}
                      </div>
                      <p className="text-[#335480] text-base line-clamp-1">
                        {int.orgao_julgador || int.orgao || "—"}
                      </p>
                      <p className="text-[#335480] text-sm mt-0.5">
                        {formatDateBR(int.data_disponibilizacao)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        data-testid={`archive-btn-${i}`}
                        onClick={(e) => { e.stopPropagation(); handleArchive(int.intimacao_id, int.arquivada); }}
                        className="p-2 rounded-lg text-[#335480] hover:text-[#003882] hover:bg-[#e5edf5] transition-colors"
                        title={int.arquivada ? "Reativar" : "Arquivar"}
                      >
                        {int.arquivada ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lazy load */}
        {hasMore && (
          <div ref={sentinelRef} className="flex justify-center mt-4">
            <Button variant="outline" disabled={loadingMore} onClick={() => load(page + 1, true)} className="border-[#D1DEEE] h-10">
              {loadingMore ? "Carregando…" : `Carregar mais (${intimacoes.length}/${total})`}
            </Button>
          </div>
        )}
        {!hasMore && total > 0 && (
          <p className="text-center text-[#335480] text-sm mt-4">{total} intimação(ões)</p>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        {selected && (
          <DialogContent className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-4 border-b border-[#D1DEEE]">
              <div className="flex items-start justify-between gap-4 pr-8">
                <div>
                  <p className="text-[#335480] text-xs uppercase tracking-widest font-medium mb-1">
                    {selected.tribunal} {selected.tribunal && selected.orgao ? "·" : ""} {selected.orgao}
                  </p>
                  <DialogTitle className="text-2xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                    {selected.numero_processo}
                  </DialogTitle>
                  {selected.titulo && (
                    <p className="text-[#335480] text-base mt-1">{selected.titulo}</p>
                  )}
                  {selected.processo_id && (
                    <button type="button" onClick={() => navigate(`/processos/${encodeURIComponent(selected.processo_id)}`)} className="text-[#003882] text-sm font-medium hover:underline mt-1">
                      Abrir processo vinculado
                    </button>
                  )}
                </div>
                <span className="shrink-0 text-xs px-3 py-1 rounded-full bg-[#e5edf5] text-[#003882] font-semibold mt-1">
                  {selected.tipo_comunicacao}
                </span>
              </div>
            </DialogHeader>

            <div className="space-y-5 pt-2">
              {/* Metadata grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-[#335480] font-medium uppercase tracking-wide text-xs mb-1">Data</p>
                  <p className="text-[#001533] font-semibold">{formatDateBR(selected.data_disponibilizacao)}</p>
                </div>
                {selected.classe && (
                  <div>
                    <p className="text-[#335480] font-medium uppercase tracking-wide text-xs mb-1">Classe</p>
                    <p className="text-[#001533] font-semibold">{selected.classe}</p>
                  </div>
                )}
                {selected.meio_completo && (
                  <div>
                    <p className="text-[#335480] font-medium uppercase tracking-wide text-xs mb-1">Meio</p>
                    <p className="text-[#001533] font-semibold">{selected.meio_completo}</p>
                  </div>
                )}
                {selected.parte_destinataria && (
                  <div className="col-span-2">
                    <p className="text-[#335480] font-medium uppercase tracking-wide text-xs mb-1">Destinatário</p>
                    <p className="text-[#001533] font-semibold">{selected.parte_destinataria}</p>
                  </div>
                )}
                {selected.prazo_sugerido_ia && (
                  <div className="col-span-2">
                    <p className="text-[#335480] font-medium uppercase tracking-wide text-xs mb-1">Prazo sugerido (IA)</p>
                    <p className="text-emerald-700 font-bold">{selected.prazo_sugerido_ia}</p>
                  </div>
                )}
              </div>

              {/* Resumo */}
              {selected.resumo && (
                <div className="bg-[#F4F7FB] border border-[#D1DEEE] rounded-lg p-4">
                  <p className="text-[#335480] text-xs font-semibold uppercase tracking-wide mb-2">Resumo</p>
                  <p className="text-[#001533] text-base leading-relaxed">{selected.resumo}</p>
                </div>
              )}

              {/* Conteúdo HTML */}
              {selected.texto && (
                <div>
                  <p className="text-[#335480] text-xs font-semibold uppercase tracking-wide mb-2">Conteúdo da Intimação</p>
                  <div
                    className="bg-white border border-[#D1DEEE] rounded-lg p-5 text-[#001533] text-base leading-relaxed max-h-[40vh] overflow-y-auto prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: selected.texto }}
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2 flex-wrap border-t border-[#D1DEEE]">
                {linkedTaskCancelled ? (
                  <>
                    <span className="inline-flex items-center rounded-lg bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">Tarefa cancelada</span>
                    <Button data-testid="modal-restore-task-btn" onClick={() => restoreCancelledTask(linkedTask)} className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold">
                      Restaurar tarefa
                    </Button>
                  </>
                ) : linkedTask ? (
                  <Button
                    data-testid="modal-open-task-btn"
                    onClick={() => openLinkedTask(linkedTask)}
                    className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                  >
                    Abrir tarefa{linkedTask.stage_name ? ` · ${linkedTask.stage_name}` : ""}
                  </Button>
                ) : (
                  <Button
                    data-testid="modal-create-task-btn"
                    onClick={openTaskForm}
                    className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                  >
                    Criar tarefa
                  </Button>
                )}
                <Button
                  data-testid="modal-archive-btn"
                  onClick={() => handleArchive(selected.intimacao_id, selected.arquivada)}
                  variant="outline"
                  className="border-[#D1DEEE] text-[#335480] hover:text-[#003882]"
                >
                  {selected.arquivada ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                  {selected.arquivada ? "Reativar" : "Arquivar"}
                </Button>
                {selected.link && (
                  <Button
                    data-testid="modal-link-btn"
                    onClick={() => window.open(selected.link, "_blank")}
                    variant="outline"
                    className="border-[#D1DEEE] text-[#335480] hover:text-[#003882]"
                  >
                    <ExternalLink size={16} />
                    Abrir no DJEN
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <TaskFormDialog
        open={!!taskForm}
        onOpenChange={(open) => { if (!open) setTaskForm(null); }}
        initialValue={taskForm}
        members={members}
        membersUnavailable={membersUnavailable}
        onSubmit={createTask}
        saving={savingTask}
        title="Criar tarefa a partir da intimação"
      />
    </Layout>
  );
}
